'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

// Backend integration: GET /api/reports/idle?range=7d
const vehicleIdle = [
  { unit: 'Unit-22', hours: 11.4, cost: 342 },
  { unit: 'Unit-11', hours: 9.8, cost: 294 },
  { unit: 'Unit-14', hours: 6.2, cost: 186 },
  { unit: 'Unit-08', hours: 4.7, cost: 141 },
  { unit: 'Unit-05', hours: 3.9, cost: 117 },
  { unit: 'Unit-19', hours: 2.8, cost: 84 },
  { unit: 'Unit-03', hours: 1.4, cost: 42 },
  { unit: 'Unit-17', hours: 0.9, cost: 27 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      <p className="text-sm font-bold text-orange-400">{payload[0]?.value} hrs idle</p>
      <p className="text-xs text-zinc-500">${payload[0]?.payload?.cost} est. fuel cost</p>
    </div>
  );
};

export default function IdleTimeReport({ dateRange }: { dateRange: string }) {
  const totalHours = vehicleIdle.reduce((s, v) => s + v.hours, 0).toFixed(1);
  const totalCost = vehicleIdle.reduce((s, v) => s + v.cost, 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Total Idle Hours</p>
          <p className="text-3xl font-bold tabular-nums text-orange-400">{totalHours}h</p>
          <p className="text-xs text-zinc-500 mt-1">Across 8 vehicles</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Est. Fuel Cost</p>
          <p className="text-3xl font-bold tabular-nums text-red-400">${totalCost.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 mt-1">@ $30/hr idle avg</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">Worst Offender</p>
          <p className="text-3xl font-bold tabular-nums text-amber-400">Unit-22</p>
          <p className="text-xs text-zinc-500 mt-1">11.4 hrs idle this week</p>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-zinc-200 mb-4">Idle Hours by Vehicle — {dateRange}</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={vehicleIdle} layout="vertical" margin={{ top: 4, right: 40, bottom: 0, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgb(39 39 42)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: 'rgb(113 113 122)' }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="unit" tick={{ fontSize: 11, fill: 'rgb(113 113 122)', fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={55} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
              {vehicleIdle.map((entry) => (
                <Cell key={`idle-${entry.unit}`} fill={entry.hours >= 8 ? '#F87171' : entry.hours >= 5 ? '#F97316' : '#F59E0B'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}